import { useState, useEffect, useCallback } from 'react';
import { campaignService } from '../lib/services/campaignService';
import { Campaign, CreateCampaignData } from '../types/campaign';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export function useCampaigns() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadCampaigns = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const data = await campaignService.getAll(user.organizationId);
      setCampaigns(data);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const createCampaign = async (data: CreateCampaignData): Promise<string> => {
    if (!user) throw new Error('No user');
    try {
      const id = await campaignService.create(data, user.id, user.displayName || user.email || 'Unknown');
      await loadCampaigns();
      toast.success('Kampány sikeresen létrehozva');
      return id;
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Hiba a kampány létrehozásakor');
      throw error;
    }
  };

  const updateCampaign = async (id: string, data: Partial<Campaign>): Promise<void> => {
    try {
      await campaignService.update(id, data);
      await loadCampaigns();
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  };

  const deleteCampaign = async (id: string): Promise<void> => {
    try {
      await campaignService.delete(id);
      await loadCampaigns();
      toast.success('Kampány törölve');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      toast.error('Hiba a kampány törlésekor');
      throw error;
    }
  };

  const duplicateCampaign = async (id: string): Promise<string> => {
    if (!user) throw new Error('No user');
    try {
      const newId = await campaignService.duplicate(id, user.id, user.displayName || user.email || 'Unknown');
      await loadCampaigns();
      toast.success('Kampány duplikálva');
      return newId;
    } catch (error) {
      console.error('Error duplicating campaign:', error);
      throw error;
    }
  };

  const updateCampaignStatus = async (id: string, status: Campaign['status']): Promise<void> => {
    try {
      await campaignService.updateStatus(id, status);
      await loadCampaigns();
    } catch (error) {
      console.error('Error updating campaign status:', error);
      throw error;
    }
  };

  return {
    campaigns,
    loading,
    error,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    duplicateCampaign,
    updateCampaignStatus,
    refresh: loadCampaigns,
  };
}
